/**
 * Grants module constants
 * Centralized definitions for regions, statuses, and call types
 */

/**
 * Polish voivodeships (regions)
 */
export const REGION_OPTIONS = [
  "dolnośląskie",
  "kujawsko-pomorskie",
  "lubelskie",
  "lubuskie",
  "łódzkie",
  "małopolskie",
  "mazowieckie",
  "opolskie",
  "podkarpackie",
  "podlaskie",
  "pomorskie",
  "śląskie",
  "świętokrzyskie",
  "warmińsko-mazurskie",
  "wielkopolskie",
  "zachodniopomorskie",
] as const;

export type Region = (typeof REGION_OPTIONS)[number];

/**
 * Grant call status options with labels
 */
export const CALL_STATUS_OPTIONS = [
  { value: "ANNOUNCED", label: "Zapowiedziany" },
  { value: "OPEN", label: "Otwarty" },
  { value: "SUSPENDED", label: "Wstrzymany" },
  { value: "CLOSED", label: "Zamknięty" },
  { value: "UNDER_REVIEW", label: "Rozpatrywany" },
  { value: "RESOLVED", label: "Rozstrzygnięty" },
] as const;

export type CallStatus = (typeof CALL_STATUS_OPTIONS)[number]["value"];

/**
 * Status labels mapping (value -> label)
 * Using Record<string, string> for runtime compatibility with API responses
 */
export const STATUS_LABELS: Record<string, string> = {
  ANNOUNCED: "Zapowiedziany",
  OPEN: "Otwarty",
  SUSPENDED: "Wstrzymany",
  CLOSED: "Zamknięty",
  UNDER_REVIEW: "Rozpatrywany",
  RESOLVED: "Rozstrzygnięty",
  EXPIRED: "Wygasły",
  CANCELLED: "Anulowany",
};

/**
 * Status colors for badges
 * Using Record<string, string> for runtime compatibility with API responses
 */
export const STATUS_COLORS: Record<string, string> = {
  ANNOUNCED: "bg-blue-100 text-blue-800 border-blue-200",
  OPEN: "bg-green-100 text-green-800 border-green-200",
  SUSPENDED: "bg-amber-100 text-amber-900 border-amber-200",
  CLOSED: "bg-neutral-200 text-neutral-800 border-neutral-300",
  UNDER_REVIEW: "bg-cyan-100 text-cyan-800 border-cyan-200",
  RESOLVED: "bg-purple-100 text-purple-800 border-purple-200",
  EXPIRED: "bg-orange-100 text-orange-800 border-orange-200",
  CANCELLED: "bg-red-100 text-red-800 border-red-200",
};

/**
 * Grant call type options with labels
 */
export const CALL_TYPE_OPTIONS = [
  { value: "COMPETITIVE", label: "Konkursowy" },
  { value: "CONTINUOUS", label: "Ciągły" },
  { value: "ALLOCATION_BASED", label: "Do wyczerpania alokacji" },
] as const;

export type CallType = (typeof CALL_TYPE_OPTIONS)[number]["value"];

/**
 * Call type labels mapping (value -> label)
 * Using Record<string, string> for runtime compatibility with API responses
 */
export const CALL_TYPE_LABELS: Record<string, string> = {
  COMPETITIVE: "Konkursowy",
  CONTINUOUS: "Ciągły",
  ALLOCATION_BASED: "Do wyczerpania",
};

/**
 * Target group labels mapping (enum value -> Polish label)
 * Mirrors backend TARGET_GROUP_LABELS from target-group.types.ts
 */
export const TARGET_GROUP_LABELS: Record<string, string> = {
  MICRO_ENTERPRISE: "Mikroprzedsiębiorstwa (do 10 prac.)",
  SMALL_ENTERPRISE: "Małe przedsiębiorstwa (10-49 prac.)",
  MEDIUM_ENTERPRISE: "Średnie przedsiębiorstwa (50-249 prac.)",
  LARGE_ENTERPRISE: "Duże przedsiębiorstwa (250+ prac.)",
  SME: "MŚP",
  ADULT: "Osoby dorosłe",
  UNEMPLOYED: "Osoby bezrobotne",
  EMPLOYEE: "Pracownicy",
  ENTREPRENEUR: "Przedsiębiorcy",
  SELF_EMPLOYED: "Osoby samozatrudnione (JDG)",
  NGO: "Organizacje pozarządowe",
  PUBLIC_INSTITUTION: "Instytucje publiczne",
  OTHER: "Inne",
};

/**
 * Get Polish label for a target group enum value
 */
export const getTargetGroupLabel = (value: string): string =>
  TARGET_GROUP_LABELS[value] || value;
